import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  List, 
  ListOrdered, 
  AlignLeft, 
  AlignCenter, 
  AlignRight,
  Type,
  Link2,
  Quote,
  Minus
} from 'lucide-react';

const RichTextEditor = ({ value, onChange, className = "", placeholder = "Enter your email content..." }) => {
  const editorRef = useRef(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [selectedText, setSelectedText] = useState('');

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    handleInput();
  };

  const handleKeyDown = (e) => {
    // Handle Enter key to create proper line breaks
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      execCommand('insertHTML', '<br><br>');
    }
  };

  const insertHeading = (level) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const selectedText = selection.toString() || 'Heading';
      
      const headingHTML = `<h${level} style="margin: 10px 0; font-weight: bold; ${
        level === 1 ? 'font-size: 24px;' : 
        level === 2 ? 'font-size: 20px;' : 
        'font-size: 16px;'
      }">${selectedText}</h${level}>`;
      
      execCommand('insertHTML', headingHTML);
    }
  };

  const insertList = (ordered = false) => {
    const selection = window.getSelection();
    const listType = ordered ? 'ol' : 'ul';
    const selectedText = selection.toString() || 'List item';
    
    const listHTML = `<${listType} style="margin: 10px 0; padding-left: 20px;">
      <li style="margin: 5px 0;">${selectedText}</li>
    </${listType}>`;
    
    execCommand('insertHTML', listHTML);
  };

  const insertDivider = () => {
    const dividerHTML = '<hr style="margin: 15px 0; border: none; border-top: 1px solid #e5e7eb;">';
    execCommand('insertHTML', dividerHTML);
  };

  const insertQuote = () => {
    const selection = window.getSelection();
    const selectedText = selection.toString() || 'Quote text';
    
    const quoteHTML = `<blockquote style="margin: 15px 0; padding: 10px 15px; border-left: 4px solid #d1d5db; background-color: #f9fafb; font-style: italic;">
      ${selectedText}
    </blockquote>`;
    
    execCommand('insertHTML', quoteHTML);
  };

  const handleLinkClick = () => {
    const selection = window.getSelection();
    const selected = selection.toString();
    
    if (selected) {
      setSelectedText(selected);
      setLinkText(selected);
    } else {
      setSelectedText('');
      setLinkText('');
    }
    
    setShowLinkDialog(true);
  };

  const insertLink = () => {
    if (linkUrl) {
      const linkHTML = `<a href="${linkUrl}" style="color: #3b82f6; text-decoration: underline;">${linkText || linkUrl}</a>`;
      
      if (selectedText) {
        execCommand('insertHTML', linkHTML);
      } else {
        execCommand('insertHTML', linkHTML);
      }
    }
    
    setShowLinkDialog(false);
    setLinkUrl('');
    setLinkText('');
    setSelectedText('');
  };

  const formatButtons = [
    {
      icon: Bold,
      command: 'bold',
      title: 'Bold (Ctrl+B)'
    },
    {
      icon: Italic,
      command: 'italic',
      title: 'Italic (Ctrl+I)'
    },
    {
      icon: Underline,
      command: 'underline',
      title: 'Underline (Ctrl+U)'
    }
  ];

  const alignButtons = [
    {
      icon: AlignLeft,
      command: 'justifyLeft',
      title: 'Align Left'
    },
    {
      icon: AlignCenter,
      command: 'justifyCenter',
      title: 'Align Center'
    },
    {
      icon: AlignRight,
      command: 'justifyRight',
      title: 'Align Right'
    }
  ];

  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="border-b border-gray-200 p-3 bg-gray-50">
        <div className="flex flex-wrap gap-1">
          {/* Text Formatting */}
          <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
            {formatButtons.map(({ icon: Icon, command, title }) => (
              <button
                key={command}
                onClick={() => execCommand(command)}
                className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                title={title}
                type="button"
              >
                <Icon className="w-4 h-4 text-gray-600" />
              </button>
            ))}
          </div>

          {/* Headings */}
          <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
            <div className="relative group">
              <button
                className="p-1.5 rounded hover:bg-gray-200 transition-colors flex items-center"
                title="Headings"
                type="button"
              >
                <Type className="w-4 h-4 text-gray-600" />
              </button>
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-1">
                  <button
                    onClick={() => insertHeading(1)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    type="button"
                  >
                    Heading 1
                  </button>
                  <button
                    onClick={() => insertHeading(2)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    type="button"
                  >
                    Heading 2
                  </button>
                  <button
                    onClick={() => insertHeading(3)}
                    className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                    type="button"
                  >
                    Heading 3
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Lists */}
          <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
            <button
              onClick={() => insertList(false)}
              className="p-1.5 rounded hover:bg-gray-200 transition-colors"
              title="Bullet List"
              type="button"
            >
              <List className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => insertList(true)}
              className="p-1.5 rounded hover:bg-gray-200 transition-colors"
              title="Numbered List"
              type="button"
            >
              <ListOrdered className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Alignment */}
          <div className="flex items-center border-r border-gray-300 pr-2 mr-2">
            {alignButtons.map(({ icon: Icon, command, title }) => (
              <button
                key={command}
                onClick={() => execCommand(command)}
                className="p-1.5 rounded hover:bg-gray-200 transition-colors"
                title={title}
                type="button"
              >
                <Icon className="w-4 h-4 text-gray-600" />
              </button>
            ))}
          </div>

          {/* Additional Tools */}
          <div className="flex items-center">
            <button
              onClick={handleLinkClick}
              className="p-1.5 rounded hover:bg-gray-200 transition-colors"
              title="Insert Link"
              type="button"
            >
              <Link2 className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={insertQuote}
              className="p-1.5 rounded hover:bg-gray-200 transition-colors"
              title="Quote"
              type="button"
            >
              <Quote className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={insertDivider}
              className="p-1.5 rounded hover:bg-gray-200 transition-colors"
              title="Insert Divider"
              type="button"
            >
              <Minus className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="p-4 min-h-[200px] focus:outline-none text-sm text-gray-700 leading-relaxed"
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        style={{
          lineHeight: '1.6',
          wordBreak: 'break-word'
        }}
        suppressContentEditableWarning={true}
        placeholder={placeholder}
      />

      {/* Link Dialog */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold mb-4">Insert Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link Text
                </label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Text to display"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowLinkDialog(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={insertLink}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
              >
                Insert Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom styles for the editor content */}
      <style jsx>{`
        div[contenteditable]:empty:before {
          content: attr(placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        
        div[contenteditable] h1, 
        div[contenteditable] h2, 
        div[contenteditable] h3 {
          margin: 10px 0;
          font-weight: bold;
        }
        
        div[contenteditable] h1 { font-size: 24px; }
        div[contenteditable] h2 { font-size: 20px; }
        div[contenteditable] h3 { font-size: 16px; }
        
        div[contenteditable] ul, 
        div[contenteditable] ol {
          margin: 10px 0;
          padding-left: 20px;
        }
        
        div[contenteditable] li {
          margin: 5px 0;
        }
        
        div[contenteditable] blockquote {
          margin: 15px 0;
          padding: 10px 15px;
          border-left: 4px solid #d1d5db;
          background-color: #f9fafb;
          font-style: italic;
        }
        
        div[contenteditable] hr {
          margin: 15px 0;
          border: none;
          border-top: 1px solid #e5e7eb;
        }
        
        div[contenteditable] a {
          color: #3b82f6;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
